import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import {
    GetTranslationsDto,
    IAccountingBasisDTO,
    IAccountingBasisExtendedDTO,
    ICurrencyExtendedDTO,
    IFinancialLineItemDTO,
    IFinancialLineItemExtendedDTO,
    IFinancialLineItemUpdateRequest,
    ILegalFormDTO,
    ILegalFormExtendedDTO,
    UpdateTranslationsDto,
} from '@modeso/types__aumico-lib-settings-be';
import { Store, select } from '@ngrx/store';
import { Observable } from 'rxjs';
import { AppState } from '../interfaces/app-state.interface';
import { IEditData } from '../interfaces/edit-data.interface';
import { IFilterOptions } from '../interfaces/filter-options.interface';
import { SettingsTranslationRequest } from '../interfaces/settings-translation-request.interface';
import {
    accountingBasisActions,
    currencyActions,
    financialLineItemActions,
    legalFormActions,
    translationsActions,
} from './../actions/settings.actions';
import {
    accountingBasisStateSelectors,
    currencyStateSelectors,
    financialLineItemStateSelectors,
    legalFormStateSelectors,
    translationsStateSelectors,
} from './../reducers/settings-root.reducer';
import { getDebug, getTag } from './../utils/debugUtil';

const filename = 'projects/aumico-lib-settings-fe/src/lib/providers/settings.provider.ts';
const debug = getDebug(filename);
const TAG = getTag(filename);

@Injectable({
    providedIn: 'root',
})
export class SettingsProvider {
    constructor(private store: Store<AppState>) {
        debug('provider constructed');
    }

    allAccountingBasis$(): Observable<IAccountingBasisExtendedDTO[]> {
        return this.store.pipe(select(accountingBasisStateSelectors.selectAll));
    }

    dispatchGetAllAccountingBasis(filterOptions?: IFilterOptions): void {
        this.store.dispatch(accountingBasisActions.getAllAccountingBasis({ payload: filterOptions }));
    }

    getActiveAccountingBasis$(): Observable<IAccountingBasisExtendedDTO> {
        return this.store.pipe(select(accountingBasisStateSelectors.selectActive));
    }

    accountingBasisError$(): Observable<HttpErrorResponse> {
        return this.store.pipe(select(accountingBasisStateSelectors.selectError));
    }

    dispatchUpdateAccountingBasis(
        data: IEditData<Pick<IAccountingBasisDTO, 'text' | 'countryISO31661Alpha2Codes'>>,
    ): void {
        this.store.dispatch(accountingBasisActions.submitEditAccountingBasis(data));
    }
    dispatchCreateAccountingBasis(item: IAccountingBasisDTO): void {
        this.store.dispatch(accountingBasisActions.submitCreateAccountingBasis({ payload: item }));
    }

    dispatchSetActiveAccountingBasis(item: IAccountingBasisExtendedDTO | string): void {
        this.store.dispatch(accountingBasisActions.setActiveAccountingBasis({ payload: item }));
    }

    allLegalForm$(): Observable<ILegalFormExtendedDTO[]> {
        return this.store.pipe(select(legalFormStateSelectors.selectAll));
    }

    dispatchGetAllLegalForm(filterOptions?: IFilterOptions): void {
        this.store.dispatch(legalFormActions.getAllLegalForm({ payload: filterOptions }));
    }

    getActiveLegalForm$(): Observable<ILegalFormExtendedDTO> {
        return this.store.pipe(select(legalFormStateSelectors.selectActive));
    }

    legalFormError$(): Observable<HttpErrorResponse> {
        return this.store.pipe(select(legalFormStateSelectors.selectError));
    }

    dispatchSubmitCreateLegalForm(item: ILegalFormDTO): void {
        this.store.dispatch(legalFormActions.submitCreateLegalForm({ payload: item }));
    }

    dispatchSubmitUpdateLegalForm(data: IEditData<Pick<ILegalFormDTO, 'text' | 'countryISO31661Alpha2Codes'>>): void {
        this.store.dispatch(legalFormActions.submitEditLegalForm(data));
    }

    dispatchSetActiveLegalForm(item: ILegalFormExtendedDTO | string): void {
        this.store.dispatch(legalFormActions.setActiveLegalForm({ payload: item }));
    }

    //#region FinancialLineItem
    allFinancialLineItem$(): Observable<IFinancialLineItemExtendedDTO[]> {
        return this.store.pipe(select(financialLineItemStateSelectors.selectAll));
    }

    dispatchGetAllFinancialLineItem(): void {
        this.store.dispatch(financialLineItemActions.getAllFinancialLineItem());
    }

    getActiveFinancialLineItem$(): Observable<IFinancialLineItemExtendedDTO> {
        return this.store.pipe(select(financialLineItemStateSelectors.selectActive));
    }

    financialLineItemError$(): Observable<HttpErrorResponse> {
        return this.store.pipe(select(financialLineItemStateSelectors.selectError));
    }

    dispatchSubmitCreateFinancialLineItem(item: IFinancialLineItemDTO): void {
        this.store.dispatch(financialLineItemActions.submitCreateFinancialLineItem({ payload: item }));
    }

    dispatchSubmitUpdateFinancialLineItem(data: IEditData<IFinancialLineItemUpdateRequest>): void {
        this.store.dispatch(financialLineItemActions.submitEditFinancialLineItem(data));
    }

    dispatchSetActiveFinancialLineItem(item: IFinancialLineItemExtendedDTO | string): void {
        this.store.dispatch(financialLineItemActions.setActiveFinancialLineItem({ payload: item }));
    }

    //#endregion FinancialLineItem

    //#region Currency
    allCurrencyItem$(): Observable<ICurrencyExtendedDTO[]> {
        return this.store.pipe(select(currencyStateSelectors.selectAll));
    }

    dispatchGetAllCurrency(): void {
        this.store.dispatch(currencyActions.getAllCurrency());
    }

    currencyError$(): Observable<HttpErrorResponse> {
        return this.store.pipe(select(currencyStateSelectors.selectError));
    }
    //#endregion Currency

    getTranslations$(): Observable<GetTranslationsDto> {
        return this.store.pipe(select(translationsStateSelectors.selectTranslations));
    }

    dispatchGetTranslations(locales: string[], untranslatedOnly: boolean): void {
        this.store.dispatch(translationsActions.getTranslations({ locales, untranslatedOnly }));
    }

    dispatchUpdateTranslations(translations: UpdateTranslationsDto): void {
        this.store.dispatch(translationsActions.updateTranslations({ payload: translations }));
    }

    dispatchGetSettingsTargetTranslations(translationRequest: SettingsTranslationRequest): void {
        this.store.dispatch(translationsActions.getSettingsTargetTranslations({ payload: translationRequest }));
    }
}
